import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  CircularProgress,
} from "@mui/material";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import DeleteIcon from "@mui/icons-material/Delete";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import axios from "axios";
import Swal from "sweetalert2";
import { getAuthCredentials } from "../../../utils/auth";
import HandleCropPromoImage from "./HandleCropPromoImage";

const MAX_SIZE = 5 * 1024 * 1024;
const ALLOWED_MIME = ["image/jpeg", "image/png", "image/gif"];

const safeRevoke = (url) => {
  try {
    if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
  } catch {}
};

const UploadLogo = ({ open, onClose, onSuccess, existingLogo = null }) => {
  const isEditing = !!existingLogo;
  const [title, setTitle] = useState("");
  const [alt, setAlt] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [type, setType] = useState("image");
  const [params, setParams] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [imageError, setImageError] = useState("");
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cropOutputType, setCropOutputType] = useState("image/png");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setTitle(existingLogo?.title || "");
    setAlt(existingLogo?.alt || "");
    setIsActive(
      existingLogo?.isActive !== undefined ? !!existingLogo.isActive : true
    );
    setType(existingLogo?.type || "image");
    setParams(existingLogo?.params || {});
    setImageFile(null);
    setImageError("");
    safeRevoke(previewUrl);
    setPreviewUrl(existingLogo?.url || "");
    safeRevoke(cropSrc);
    setCropSrc(null);
    setCropperOpen(false);
  }, [open, existingLogo]);

  useEffect(() => {
    return () => {
      safeRevoke(previewUrl);
      safeRevoke(cropSrc);
    };
  }, [previewUrl, cropSrc]);

  const handlePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_MIME.includes(file.type)) {
      setImageError(
        "Unsupported file type. Only JPEG, PNG, and GIF are allowed."
      );
      e.target.value = "";
      return;
    }
    if (file.size > MAX_SIZE) {
      setImageError("File size exceeds 5 MB.");
      e.target.value = "";
      return;
    }
    setImageError("");

    if (file.type === "image/png") setCropOutputType("image/png");
    else if (file.type === "image/jpeg") setCropOutputType("image/jpeg");
    else if (file.type === "image/gif") {
      setCropOutputType("image/png");
    } else {
      setCropOutputType("image/png");
    }

    safeRevoke(cropSrc);
    setCropSrc(URL.createObjectURL(file));
    setCropperOpen(true);
  };

  const handleCropSave = (blob) => {
    const ext =
      blob.type === "image/png"
        ? "png"
        : blob.type === "image/jpeg"
        ? "jpg"
        : blob.type === "image/webp"
        ? "webp"
        : "bin";
    const newFile = new File([blob], `logo.${ext}`, { type: blob.type });

    setImageFile(newFile);
    safeRevoke(previewUrl);
    setPreviewUrl(URL.createObjectURL(blob));
    setCropperOpen(false);
    safeRevoke(cropSrc);
    setCropSrc(null);
  };

  const handleCropCancel = () => {
    setCropperOpen(false);
    safeRevoke(cropSrc);
    setCropSrc(null);
  };

  const clearPicked = () => {
    setImageFile(null);
    safeRevoke(previewUrl);
    setPreviewUrl(existingLogo?.url || "");
    setImageError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const submit = async () => {
    try {
      setSaving(true);
      const { token } = await getAuthCredentials();
      const url = `${process.env.REACT_APP_BACKEND_URL}/v1/api/promotion/brands/logo`;
      const method = isEditing ? "put" : "post";
      const form = new FormData();
      if (imageFile) form.append("media", imageFile);

      const resolvedType =
        imageFile?.type === "image/gif" || existingLogo?.type === "gif"
          ? "gif"
          : "image";

      form.append(
        "data",
        JSON.stringify({
          type: resolvedType,
          isActive: !!isActive,
          alt: alt || "",
          title: title || "",
          params: params || {},
        })
      );

      await axios({
        url,
        method,
        headers: { Authorization: `Bearer ${token}` },
        data: form,
      });

      Swal.fire({
        icon: "success",
        title: isEditing ? "Logo updated" : "Logo uploaded",
        timer: 1300,
        showConfirmButton: false,
      });
      onSuccess?.();
      onClose?.();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Upload failed",
        text: err?.response?.data?.message || "Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const disabledSave = useMemo(() => {
    if (!isEditing) return !imageFile || saving;
    return saving;
  }, [isEditing, imageFile, saving]);

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{isEditing ? "Edit Logo" : "Upload Logo"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<PhotoCamera />}
                  disabled={saving}
                >
                  Choose Image
                  <input
                    ref={inputRef}
                    hidden
                    accept="image/*"
                    type="file"
                    onChange={handlePick}
                  />
                </Button>
              </Grid>
              {previewUrl ? (
                <Grid item>
                  <img
                    src={previewUrl}
                    alt="Logo preview"
                    style={{
                      width: 120,
                      height: 120,
                      objectFit: "contain",
                      borderRadius: 8,
                      border: "1px solid rgba(0,0,0,0.1)",
                      background: "transparent",
                    }}
                  />
                </Grid>
              ) : (
                <Grid item>
                  <BrokenImageIcon color="disabled" />
                </Grid>
              )}
              {!isEditing && (imageFile || previewUrl) && (
                <Grid item>
                  <Tooltip title="Remove selected">
                    <span>
                      <IconButton
                        onClick={clearPicked}
                        disabled={saving}
                        color="error"
                        aria-label="Remove selected"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
              )}
            </Grid>

            {imageError && (
              <Typography color="error" variant="body2">
                {imageError}
              </Typography>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  disabled={saving}
                />
              }
              label="Active"
            />

            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              disabled={saving}
            />
            <TextField
              label="Alt text"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              fullWidth
              helperText="Accessibility text"
              disabled={saving}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            variant="contained"
            disabled={disabledSave}
            startIcon={saving ? <CircularProgress size={18} /> : null}
          >
            {isEditing ? "Save Changes" : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      <HandleCropPromoImage
        open={cropperOpen}
        imageSrc={cropSrc}
        onCancel={handleCropCancel}
        onSave={handleCropSave}
        aspect={1}
        outputType={cropOutputType}
      />
    </>
  );
};

export default UploadLogo;
